import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  Catalogue,
  CreateCatalogueRequest,
} from "@shared-core/data/catalogue-types";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../app/components/primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import { Input } from "../app/components/primitives/input";
import { Label } from "../app/components/primitives/label";
import { ownershipLabel } from "@shared-core/domain/catalogue-ownership";
import { RegistryTile } from "../app/components/patterns/RegistryTile";
import { Plus } from "lucide-react";

// Catalogues index — the registry of catalogues. Tiles open the catalogue
// management grid for that scope. Live-data backed via DataAdapter.

export function CataloguesPage() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [catalogues, setCatalogues] = useState<Catalogue[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    adapter
      .listCataloguesForManagement()
      .then(setCatalogues)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleCreate(req: CreateCatalogueRequest) {
    try {
      const created = await adapter.createCatalogue(req);
      setCatalogues((prev) => [...(prev ?? []), created]);
      setDialogOpen(false);
      toast.success(
        created.privateTo
          ? `Catalogue "${created.name}" created and allocated to its customer.`
          : `Catalogue "${created.name}" created. Allocate it to customers on their records in One.`,
      );
    } catch {
      toast.error("Failed to create catalogue");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1>Catalogues</h1>
          <p className="text-sm text-muted-foreground">
            The catalogues in force. A customer with no catalogue allocated sees
            the core range; allocate catalogues on the customer&apos;s record.
          </p>
        </div>
        <Button size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="size-4" /> New catalogue
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-sm font-medium">Catalogues could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={load}>
            Retry
          </Button>
        </div>
      ) : !catalogues || catalogues.length === 0 ? (
        <p className="text-sm text-muted-foreground">No catalogues yet.</p>
      ) : (
        <div className="space-y-3">
          {catalogues.map((c) => (
            <RegistryTile
              key={c.id}
              title={c.name}
              ownership={ownershipLabel(c.ownedBy)}
              scope={
                c.audience === "full_range"
                  ? "full_range"
                  : c.privateTo
                    ? { privateTo: c.privateTo.customerName }
                    : "shared"
              }
              onOpen={() =>
                navigate(paths.settingsCatalogueEdit({ catalogueId: c.id }))
              }
            />
          ))}
        </div>
      )}

      <NewCatalogueDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreate={handleCreate}
      />
    </div>
  );
}

function NewCatalogueDialog({
  open,
  onOpenChange,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (req: CreateCatalogueRequest) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [privacy, setPrivacy] = useState<"shared" | "private">("shared");
  const [customerCode, setCustomerCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    customerCode?: string;
  }>({});

  function reset() {
    setName("");
    setPrivacy("shared");
    setCustomerCode("");
    setErrors({});
  }

  function validate(): boolean {
    const next: typeof errors = {};
    if (!name.trim()) next.name = "Name is required";
    if (privacy === "private" && !customerCode.trim())
      next.customerCode =
        "Enter the code of the customer the catalogue is private to";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onCreate({
        name: name.trim(),
        privateToCustomerCode:
          privacy === "private" ? customerCode.trim() : undefined,
      });
      reset();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New catalogue</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="catalogue-name">Name</Label>
            <Input
              id="catalogue-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Education, Ramsay Healthcare"
              aria-invalid={!!errors.name}
              aria-describedby={
                errors.name ? "catalogue-name-error" : undefined
              }
            />
            {errors.name && (
              <p id="catalogue-name-error" className="text-sm text-destructive">
                {errors.name}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Who can have it</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="privacy"
                  value="shared"
                  checked={privacy === "shared"}
                  onChange={() => setPrivacy("shared")}
                />
                <span className="text-sm">Shared</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="privacy"
                  value="private"
                  checked={privacy === "private"}
                  onChange={() => setPrivacy("private")}
                />
                <span className="text-sm">Private to a customer</span>
              </label>
            </div>
          </div>

          {privacy === "private" && (
            <div className="space-y-1.5">
              <Label htmlFor="catalogue-customer-code">Customer code</Label>
              <Input
                id="catalogue-customer-code"
                value={customerCode}
                onChange={(e) => setCustomerCode(e.target.value)}
                placeholder="e.g. RAM1379 or CUS-K48193"
                aria-invalid={!!errors.customerCode}
                aria-describedby={
                  errors.customerCode
                    ? "catalogue-customer-code-error"
                    : undefined
                }
              />
              {errors.customerCode && (
                <p
                  id="catalogue-customer-code-error"
                  className="text-sm text-destructive"
                >
                  {errors.customerCode}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create catalogue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
